import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Card,
  Row,
  Col,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined
} from '@ant-design/icons';
import { Student, StudentFilters, studentAPI } from '../../services/studentAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import StudentForm from './StudentForm';

const { Search } = Input;

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface StudentListProps {
  selectedCompany: SelectedCompany | null;
}

const StudentList: React.FC<StudentListProps> = ({ selectedCompany }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [filters, setFilters] = useState<StudentFilters>({});

  const educationLevelLabels = {
    'orta_tehsil': 'Orta təhsil',
    'bakalavr': 'Bakalavr',
    'magistr': 'Magistr',
    'doktorant': 'Doktorant'
  };

  useEffect(() => {
    if (selectedCompany) {
      loadStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedCompany]);

  const loadStudents = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const companyFilters = { ...filters, company: selectedCompany.id };
      const response = await studentAPI.getStudents(companyFilters);
      if (response.success && response.data) {
        setStudents(response.data);
      } else {
        message.error(response.message || 'Tələbələr yüklənmədi');
      }
    } catch (error) {
      message.error('Server xətası');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await studentAPI.deleteStudent(id);
      if (response.success) {
        message.success(response.message || 'Tələbə silindi');
        loadStudents();
      } else {
        message.error(response.message || 'Tələbə silinmədi');
      }
    } catch (error) {
      message.error('Server xətası');
    }
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setIsModalVisible(true);
  };

  const handleAdd = () => {
    setEditingStudent(null);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingStudent(null);
  };

  const handleFormSuccess = () => {
    loadStudents();
    handleModalClose();
  };

  const columns = [
    {
      title: 'Tam ad',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text: string, record: Student) => (
        <div>
          <div className="font-medium">{text}</div>
        </div>
      ),
    },
    {
      title: 'FIN',
      dataIndex: 'fin_code',
      key: 'fin_code',
    },
    {
      title: 'Seriya',
      dataIndex: 'id_number',
      key: 'id_number',
    },
    {
      title: 'Nömrə',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Yaş',
      dataIndex: 'age',
      key: 'age',
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: Student) => {
        const getStatusColor = (status?: string) => {
          if (!status) return 'default';
          switch (status) {
            case 'aktiv':
              return 'green';
            case 'passiv':
              return 'red';
            case 'mezun':
              return 'blue';
            case 'donmuş':
              return 'orange';
            default:
              return 'default';
          }
        };

        const getStatusLabel = (status?: string) => {
          if (!status) return 'Melum deyil';
          switch (status) {
            case 'aktiv':
              return 'Aktiv';
            case 'passiv':
              return 'Passiv';
            case 'mezun':
              return 'Mezun';
            case 'donmuş':
              return 'Donmus';
            default:
              return status;
          }
        };

        return (
          <Tag color={getStatusColor(record.student_status)}>
            {getStatusLabel(record.student_status)}
          </Tag>
        );
      },
    },
    {
      title: 'Əməliyyatlar',
      key: 'actions',
      render: (record: Student) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Popconfirm
            title="Tələbəni silmək istədiyinizə əminsiniz?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Bəli"
            cancelText="Xeyr"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Şirkət seçilmədiyi halda xəbərdarlıq */}
      {!selectedCompany && (
        <Alert
          message="Şirkət seçilməyib"
          description="Tələbələri görmək üçün yuxarıdan şirkət seçin."
          type="warning"
          showIcon
          className="mb-6"
        />
      )}

      {selectedCompany && (
        <>
          {/* Filtrlər və Əlavə et düyməsi */}
          <Card className="mb-4">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <Search
                  placeholder="Tələbə axtar..."
                  allowClear
                  onSearch={(value) => setFilters({ ...filters, search: value })}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Select
                  placeholder="Tələbə statusu"
                  allowClear
                  style={{ width: '100%' }}
                  onChange={(value) => setFilters({ ...filters, student_status: value })}
                  options={[
                    { value: 'aktiv', label: 'Aktiv' },
                    { value: 'passiv', label: 'Passiv' },
                    { value: 'mezun', label: 'Mezun' },
                    { value: 'donmuş', label: 'Donmus' }
                  ]}
                />
              </Col>
              <Col xs={24} sm={8} md={4}>
                <Select
                  placeholder="Ödəniş statusu"
                  allowClear
                  style={{ width: '100%' }}
                  onChange={(value) => setFilters({ ...filters, payment_status: value })}
                  options={[
                    { value: 'ödənilib', label: 'Ödənilib' },
                    { value: 'gecikir', label: 'Gecikir' },
                    { value: 'gözləyir', label: 'Gözləyir' }
                  ]}
                />
              </Col>
              <Col xs={24} sm={24} md={4} className="text-right">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                  size="large"
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  Tələbə Əlavə Et
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Tələbələr cədvəli */}
          <Card>
            <Table
              columns={columns}
              dataSource={students}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} tələbə`,
              }}
            />
          </Card>

          {/* Tələbə əlavə/redaktə formu */}
          <StudentForm
            visible={isModalVisible}
            student={editingStudent}
            onCancel={handleModalClose}
            onSuccess={handleFormSuccess}
            selectedCompany={selectedCompany}
          />
        </>
      )}
    </div>
  );
};

export default StudentList;